import { ICellRendererParams } from 'ag-grid-community'
import { useTranslations } from 'next-intl'
import { memo } from 'react'
import { ReviewStatus } from '@skop-app/lib-types'
import { cx } from 'class-variance-authority'

import { Translator } from '@/types'
import { OPTION_RETURNED_STATUS } from '@/services/property/option.status'
import { getOptionCreationReviewLatestMessage } from '@/services/property/option.workflow'
import type { OptionGridContext } from '@/features/property/optionGrid.type'
import type { OptionRow } from '@/services/property/property.type'

const getStatusConfig = (status: string | undefined, t: Translator) => {
  switch (status) {
    case ReviewStatus.ACCEPTED:
      return {
        text: t('Review.accepted'),
        bgColor: 'bg-lime-100 border-lime-300',
      }
    case ReviewStatus.REJECTED:
      return {
        text: t('Review.rejected'),
        bgColor: 'bg-rose-100 border-rose-300',
      }
    case OPTION_RETURNED_STATUS:
      return {
        text: t('Property.returned'),
        bgColor: 'bg-amber-100 border-amber-300',
      }
    case ReviewStatus.PENDING:
      return {
        text: t('Review.pending'),
        bgColor: 'bg-orange-100 border-orange-300',
      }
    default:
      return {
        text: t('Review.not-reviewed'),
        bgColor: 'bg-neutral-100 border-neutral-300',
      }
  }
}

export const ReviewPillRenderer = memo(
  (params: ICellRendererParams<OptionRow, unknown, OptionGridContext>) => {
    const t = useTranslations()

    if (params.data?.id?.includes('new')) return null

    const returnedReason =
      params.value === OPTION_RETURNED_STATUS
        ? getOptionCreationReviewLatestMessage({
            elementId: params.data?.id,
            reviewsById: params.context?.reviewsById,
          })
        : undefined
    const { text, bgColor } = getStatusConfig(
      typeof params.value === 'string' ? params.value : undefined,
      t,
    )
    const renderedText =
      typeof params.valueFormatted === 'string' &&
      params.valueFormatted.length > 0
        ? params.valueFormatted
        : text

    return (
      <span
        className={cx(
          `rounded border px-1.5 py-1 text-xs text-slate-500`,
          bgColor,
        )}
        title={returnedReason}
      >
        {renderedText}
      </span>
    )
  },
)

ReviewPillRenderer.displayName = 'ReviewPillRenderer'
